import { useMemo, useState } from "react";
import { Search, GraduationCap, Users, School } from "lucide-react";
import { NOTIFICATION_AUDIENCE_MODES, NOTIFICATION_TYPES } from "./notificationHelpers";
import { NotificationTypeIcon } from "./NotificationParts";

function teacherLabel(teacher) {
  return `${teacher?.firstName || ""} ${teacher?.lastName || ""}`.trim() || "Teacher";
}

function parentLabel(parent) {
  const father = `${parent?.fatherFirstName || ""} ${parent?.fatherLastName || ""}`.trim();
  return father || parent?.email || "Parent";
}

function RecipientChecklist({ title, icon: Icon, items, selectedIds, onChange, searchPlaceholder }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => item.label.toLowerCase().includes(term));
  }, [items, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((item) => selectedIds.includes(item.value));

  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredSet = new Set(filtered.map((item) => item.value));
      onChange(selectedIds.filter((id) => !filteredSet.has(id)));
      return;
    }
    onChange(Array.from(new Set([...selectedIds, ...filtered.map((item) => item.value)])));
  };

  return (
    <div className="notif-recipient-panel">
      <div className="notif-recipient-panel-head">
        <Icon size={16} />
        <strong>{title}</strong>
        <span>{selectedIds.length} selected</span>
      </div>
      <label className="notif-recipient-search">
        <Search size={14} />
        <input
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <button type="button" className="notif-recipient-select-all" onClick={toggleAllFiltered}>
        {allFilteredSelected ? "Clear visible" : "Select visible"}
      </button>
      <div className="notif-recipient-list">
        {filtered.length === 0 ? (
          <p className="notif-recipient-empty">No matches found</p>
        ) : (
          filtered.map((item) => {
            const checked = selectedIds.includes(item.value);
            return (
              <label key={item.value} className={`notif-recipient-item${checked ? " is-checked" : ""}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(event) => {
                    if (event.target.checked) {
                      onChange([...selectedIds, item.value]);
                    } else {
                      onChange(selectedIds.filter((id) => id !== item.value));
                    }
                  }}
                />
                <span className="notif-recipient-copy">
                  <strong>{item.label}</strong>
                  {item.hint ? <em>{item.hint}</em> : null}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function NotificationAudiencePicker({
  sendTo,
  onSendToChange,
  teachers = [],
  parents = [],
  classrooms = [],
  selectedTeachers = [],
  selectedParents = [],
  selectedClassroom = "",
  onTeachersChange,
  onParentsChange,
  onClassroomChange,
  disabled = false,
}) {
  const teacherOptions = useMemo(
    () =>
      teachers.map((teacher) => ({
        value: teacher._id,
        label: teacherLabel(teacher),
        hint: teacher.classroom?.classroomName ? `Class: ${teacher.classroom.classroomName}` : "",
      })),
    [teachers]
  );

  const parentOptions = useMemo(
    () =>
      parents.map((parent) => ({
        value: parent._id,
        label: parentLabel(parent),
        hint: parent.email || "",
      })),
    [parents]
  );

  const selectedClass = classrooms.find((item) => String(item._id) === String(selectedClassroom));

  return (
    <div className="notif-audience-picker">
      <div className="notif-audience-grid">
        {NOTIFICATION_AUDIENCE_MODES.map((mode) => {
          const active = sendTo === mode.value;
          return (
            <button
              key={mode.value}
              type="button"
              disabled={disabled}
              className={`notif-audience-card${active ? " is-active" : ""}`}
              onClick={() => onSendToChange(mode.value)}
            >
              <strong>{mode.label}</strong>
              <span>{mode.description}</span>
            </button>
          );
        })}
      </div>

      {sendTo === "CUSTOM" ? (
        <div className="notif-recipient-grid">
          <RecipientChecklist
            title="Teachers"
            icon={GraduationCap}
            items={teacherOptions}
            selectedIds={selectedTeachers}
            onChange={onTeachersChange}
            searchPlaceholder="Search teachers..."
          />
          <RecipientChecklist
            title="Parents"
            icon={Users}
            items={parentOptions}
            selectedIds={selectedParents}
            onChange={onParentsChange}
            searchPlaceholder="Search parents..."
          />
        </div>
      ) : null}

      {sendTo === "CLASSROOM" ? (
        <div className="notif-classroom-panel">
          <div className="notif-recipient-panel-head">
            <School size={16} />
            <strong>Select class</strong>
          </div>
          <select
            className="notif-classroom-select"
            value={selectedClassroom || ""}
            disabled={disabled}
            onChange={(event) => onClassroomChange(event.target.value)}
          >
            <option value="">Choose a class...</option>
            {classrooms.map((classroom) => (
              <option key={classroom._id} value={classroom._id}>
                {classroom.classroomName}
                {classroom.classroomGrade ? ` · Grade ${classroom.classroomGrade}` : ""}
              </option>
            ))}
          </select>
          {selectedClass ? (
            <p className="notif-audience-summary">
              Parents of students in <strong>{selectedClass.classroomName}</strong> and the homeroom
              teacher will receive this notification.
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="notif-audience-summary">
        Delivery runs in the background so large sends do not slow down the admin portal.
      </p>
    </div>
  );
}

export function NotificationTypePicker({ value, onChange, disabled = false }) {
  return (
    <div className="notif-type-picker">
      {NOTIFICATION_TYPES.map((type) => {
        const active = value === type.value;
        return (
          <button
            key={type.value}
            type="button"
            disabled={disabled}
            className={`notif-type-option${active ? " is-active" : ""}`}
            onClick={() => onChange(type.value)}
          >
            <NotificationTypeIcon type={type.value} size={18} />
            <span>{type.label}</span>
          </button>
        );
      })}
    </div>
  );
}
